import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FiEye, FiFilter, FiDownload, FiSearch } from 'react-icons/fi';
import auditLogService from '../../services/auditLogService';
import './AuditLogs.css';

const AuditLogs = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    action: '',
    entity_type: '',
    severity: '',
    status: '',
    search: ''
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['auditLogs', filters],
    queryFn: () => auditLogService.getAllAuditLogs(filters),
    placeholderData: (previousData) => previousData
  });

  const auditLogs = data?.data?.auditLogs || [];
  const pagination = data?.data?.pagination || {};

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleExport = async () => {
    try {
      const result = await auditLogService.exportAuditLogs(filters);
      const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString()}.json`;
      a.click();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getActionBadgeClass = (action) => {
    const classes = {
      CREATE: 'badge-success',
      UPDATE: 'badge-info',
      DELETE: 'badge-danger',
      LOGIN: 'badge-primary',
      LOGOUT: 'badge-secondary',
      APPROVE: 'badge-success',
      REJECT: 'badge-danger'
    };
    return classes[action] || 'badge-secondary';
  };

  const getSeverityBadgeClass = (severity) => {
    const classes = {
      LOW: 'badge-info',
      MEDIUM: 'badge-warning',
      HIGH: 'badge-danger',
      CRITICAL: 'badge-critical'
    };
    return classes[severity] || 'badge-secondary';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      SUCCESS: 'badge-success',
      FAILURE: 'badge-danger',
      PARTIAL: 'badge-warning'
    };
    return classes[status] || 'badge-secondary';
  };

  if (isLoading) return <div className="loading">Loading audit logs...</div>;
  if (error) return <div className="error">Error loading audit logs: {error.message}</div>;

  return (
    <div className="audit-logs-container">
      <div className="page-header">
        <h1>Audit Logs</h1>
        <button onClick={handleExport} className="btn btn-secondary">
          <FiDownload /> Export
        </button>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="">
          <FiSearch className="filter-icon " />
          <input
            type="text"
            name="search"
            placeholder="Search description"
            value={filters.search}
            onChange={handleFilterChange}
            className="filter-input "
          />
        </div>

        <select name="action" value={filters.action} onChange={handleFilterChange} className="filter-select custom-filter">
          <option value="">All Actions</option>
          <option value="CREATE">Create</option>
          <option value="READ">Read</option>
          <option value="UPDATE">Update</option>
          <option value="DELETE">Delete</option>
          <option value="LOGIN">Login</option>
          <option value="LOGOUT">Logout</option>
          <option value="APPROVE">Approve</option>
          <option value="REJECT">Reject</option>
        </select>

        <select name="severity" value={filters.severity} onChange={handleFilterChange} className="filter-select custom-filter">
          <option value="">All Severities</option>
          <option value="LOW">Low</option>
          <option value="MEDIUM">Medium</option>
          <option value="HIGH">High</option>
          <option value="CRITICAL">Critical</option>
        </select>

        <select name="status" value={filters.status} onChange={handleFilterChange} className="filter-select custom-filter">
          <option value="">All Statuses</option>
          <option value="SUCCESS">Success</option>
          <option value="FAILURE">Failure</option>
          <option value="PARTIAL">Partial</option>
        </select>

        <input
          type="text"
          name="entity_type"
          placeholder="Entity Type"
          value={filters.entity_type}
          onChange={handleFilterChange}
          className="filter-input"
        />
      </div>
      </div>

      {/* Audit Logs Table */}
      <div className="table-container">
        <table className="audit-logs-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User</th>
              <th>Action</th>
              <th>Entity</th>
              <th>Description</th>
              <th>Severity</th>
              <th>Status</th>
              <th>IP Address</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {auditLogs.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">No audit logs found</td>
              </tr>
            ) : (
              auditLogs.map((log) => (
                <tr key={log.id}>
                  <td>{new Date(log.created_at).toLocaleString()}</td>
                  <td>
                    {log.user ? (
                      <div className="user-info">
                        <span>{log.user.first_name} {log.user.last_name}</span>
                        <small>{log.user.email}</small>
                      </div>
                    ) : (
                      <span className="text-muted">System</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${getActionBadgeClass(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td>
                    <div className="entity-info">
                      <span className="entity-type">{log.entity_type}</span>
                      {log.entity_name && <small>{log.entity_name}</small>}
                    </div>
                  </td>
                  <td className="description-cell">{log.description}</td>
                  <td>
                    <span className={`badge ${getSeverityBadgeClass(log.severity)}`}>
                      {log.severity}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(log.status)}`}>
                      {log.status}
                    </span>
                  </td>
                  <td>{log.ip_address || '-'}</td>
                  <td>
                    <Link to={`/app/audit-logs/${log.id}`} className="btn-icon" title="View Details">
                      <FiEye />
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;


